import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, CheckCircle2, Clock, Truck, PackageCheck, XCircle, Loader2, MapPin, User2 } from "lucide-react";
import api from "@/lib/api";
import { formatJMD, fmtDate, PAYMENT_LABEL, STATUS_META } from "@/lib/format";
import { Navbar } from "@/components/public/Navbar";
import { Footer } from "@/components/public/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STEPS = [
  { k: "pending", label: "Order received", icon: Clock },
  { k: "confirmed", label: "Confirmed", icon: CheckCircle2 },
  { k: "out_for_delivery", label: "Out for delivery", icon: Truck },
  { k: "delivered", label: "Delivered", icon: PackageCheck },
];

export default function TrackPage() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    if (!code || !phone) return setError("Enter your order code and phone number.");
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get("/orders/track", { params: { code, phone } });
      setOrder(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not find that order.");
    } finally { setLoading(false); }
  };

  const stepIdx = order ? STEPS.findIndex((s) => s.k === order.status) : -1;
  const cancelled = order?.status === "cancelled";

  return (
    <div className="bg-sky-950 min-h-screen" data-testid="track-page">
      <Navbar solid />
      <section className="relative pt-32 pb-24 cw-ocean-gradient overflow-hidden">
        <div className="absolute -top-10 right-1/4 w-96 h-96 bg-cyan-400 rounded-full cw-blob opacity-30" />
        <div className="max-w-3xl mx-auto px-5 lg:px-8 relative">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-300">Order Tracking</span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-3">Where's my water?</h1>
            <p className="mt-4 text-sky-100/80">Enter the order code from your receipt and the phone number you ordered with.</p>
          </div>

          <form onSubmit={lookup} data-testid="track-form" className="mt-10 bg-white rounded-3xl p-6 lg:p-8 shadow-2xl grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="code">Order code</Label>
              <Input id="code" data-testid="track-input-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CW-1234" className="h-11 rounded-xl font-mono-cw uppercase" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tphone">Phone number</Label>
              <Input id="tphone" data-testid="track-input-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="876-555-0100" className="h-11 rounded-xl" />
            </div>
            <Button type="submit" data-testid="track-submit" disabled={loading} className="h-11 rounded-xl px-6 bg-sky-600 hover:bg-sky-500 text-white font-bold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" /> Track</>}
            </Button>
            {error && <p data-testid="track-error" className="sm:col-span-3 text-sm text-rose-600 font-medium">{error}</p>}
          </form>

          {order && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="track-result" className="mt-8 bg-white rounded-3xl p-6 lg:p-8 shadow-2xl">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Order</div>
                  <div className="font-mono-cw text-2xl font-extrabold text-sky-700">{order.order_code}</div>
                  <div className="text-sm text-slate-500 mt-1">Placed {fmtDate(order.created_at)} · {PAYMENT_LABEL[order.payment_method]}</div>
                </div>
                <span data-testid="track-status-badge" className={`px-3 py-1.5 rounded-full border text-sm font-bold ${STATUS_META[order.status]?.cls}`}>{STATUS_META[order.status]?.label}</span>
              </div>

              {cancelled ? (
                <div className="mt-6 flex items-center gap-3 rounded-2xl bg-rose-50 border border-rose-200 p-4 text-rose-700 text-sm">
                  <XCircle className="w-6 h-6" /> This order was cancelled. Call us if you think this is a mistake.
                </div>
              ) : (
                <ol className="mt-8 grid grid-cols-4 gap-2" data-testid="track-timeline">
                  {STEPS.map((s, i) => {
                    const done = i <= stepIdx;
                    const current = i === stepIdx;
                    return (
                      <li key={s.k} className="relative flex flex-col items-center text-center" data-testid={`track-step-${s.k}`} data-done={done}>
                        {i > 0 && <div className={`absolute top-5 right-1/2 w-full h-1 -z-0 ${i <= stepIdx ? "bg-sky-500" : "bg-slate-200"}`} />}
                        <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center ${done ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-400"} ${current ? "ring-4 ring-sky-200 cw-ripple" : ""}`}>
                          <s.icon className="w-5 h-5" />
                        </div>
                        <div className={`mt-2 text-[11px] sm:text-xs font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>{s.label}</div>
                      </li>
                    );
                  })}
                </ol>
              )}

              <div className="grid sm:grid-cols-2 gap-4 mt-8 text-sm">
                <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
                  <div className="flex items-start gap-2 text-slate-700"><MapPin className="w-4 h-4 mt-0.5 text-sky-600 shrink-0" />{order.address}</div>
                  <div className="flex items-center gap-2 text-slate-700"><Clock className="w-4 h-4 text-sky-600" /> Delivery date: <strong>{fmtDate(order.delivery_date)}</strong></div>
                  {order.run && <div className="flex items-center gap-2 text-slate-700" data-testid="track-driver"><User2 className="w-4 h-4 text-sky-600" /> Driver: <strong>{order.run.driver}</strong></div>}
                </div>
                <div className="bg-slate-50 rounded-2xl p-4">
                  {order.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-slate-700"><span>{it.quantity}× {it.product_name}</span><span className="font-mono-cw">{formatJMD(it.unit_price * it.quantity)}</span></div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 mt-2 pt-2 font-bold text-slate-900"><span>Total</span><span className="font-mono-cw text-sky-700">{formatJMD(order.total_amount)}</span></div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
